import { AxiomReportType } from '@/enums/axiom-report.enum'
import { IGfpaDetailsDTO } from '@/interfaces/gfpaReport.interface'
import { IDetailsDataMap } from '@/interfaces/rvDetails.interface'
import { formatNumber } from '@/utils/utils'
import { gfpaColumnFieldForTableChartLinkingValue } from '@constants/gfpa.constant'
import { gfrColumnFieldForTableChartLinkingValue } from '@constants/gfr.constant'
import { Card, CardContent, CardHeader, Typography } from '@mui/material'
import { getColumnDetail } from '@utils/grid-utils/columnUtils'
import { formatCellValue } from '@utils/tableGridUtils'
import Highcharts, { PointOptionsObject } from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import drilldown from 'highcharts/modules/drilldown'
import React, { useEffect, useRef, useState } from 'react'

drilldown(Highcharts)

interface CustomPointOptionsObject extends PointOptionsObject {
  otherDetails?: IGfpaDetailsDTO
}
interface ExtendedChart extends Highcharts.Chart {
  drilldownLevels: Array<any>
}
export const GFPADetailsChart: React.FC<{
  data: IDetailsDataMap
  expandedRowGroupInfo: { name: string; level: number }
  reportType: string
  gfpaSummarySelectedSeriesName: string
}> = ({
  data,
  expandedRowGroupInfo,
  reportType,
  gfpaSummarySelectedSeriesName,
}) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null)
  const [currentLevel, setCurrentLevel] = useState<number>(0)
  const agGridRowGroupClickRef = useRef(false)
  const [chartOptions, setChartOptions] = useState({
    chart: {
      type: 'pie',
      height: 450,
      events: {
        drillup: () => {
          if (agGridRowGroupClickRef.current) {
            agGridRowGroupClickRef.current = false
            setCurrentLevel((prevLevel) => prevLevel - 1)
          }
        },
      },
    },
    credits: {
      enabled: false,
    },
    title: {
      text: '',
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      series: {
        borderRadius: 5,
        dataLabels: [
          {
            enabled: true,
            distance: 15,
            format: '{point.labelToDisplay}',
          },
        ],
      },
    },
    tooltip: {
      useHTML: true,
      headerFormat:
        '<div style="text-align: center; font-size: 11px;">{series.name}</div><br>',
      pointFormatter: function (this: CustomPointOptionsObject): string {
        const groupKey =
          reportType === AxiomReportType.GFR
            ? gfrColumnFieldForTableChartLinkingValue[0]
            : gfpaColumnFieldForTableChartLinkingValue[0]
        const formattedValue = formatCellValue(
          formatNumber(this.y),
          getColumnDetail(groupKey, reportType).format,
        )
        let tooltip = `<span style="color:${this.color}">${this.name}</span>: <b>${formattedValue}</b><br>`
        if (this.otherDetails && this.otherDetails.security_id) {
          tooltip += `<span style="color:${this.color}">SecurityId</span>: <b>${this.otherDetails.security_id}</b><br>`
        }
        return tooltip
      },
    },
  })

  useEffect(() => {
    // Only update chart if we have valid data
    if (!data?.seriesData || data.seriesData.length === 0) {
      return
    }
    
    const chartRefObj = chartRef.current?.chart as ExtendedChart
    if (chartRefObj && data.seriesData) {
      const len = chartRefObj.drilldownLevels?.length
      for (let i = 0; i < len; i++) {
        chartRefObj.drillUp()
      }
    }
    setChartOptions((chartPrevOptions) => ({
      ...chartPrevOptions,
      series: [
        {
          name: 'All',
          colorByPoint: true,
          data: data.seriesData,
        },
      ],
      drilldown: {
        series: data.drilldownData,
      },
    }))
  }, [data])

  useEffect(() => {
    const chartRefObj = chartRef.current?.chart as ExtendedChart
    if (chartRefObj && chartRefObj.series[0]) {
      const targetLevel = expandedRowGroupInfo.level
      const pointToDrilldown = chartRefObj.series[0]?.data?.find(
        (point: any) => point?.drilldown === expandedRowGroupInfo.name,
      )
      if (pointToDrilldown) {
        const drilldownSeries = data.drilldownData?.find(
          (series) => series.id === expandedRowGroupInfo.name,
        )
        if (drilldownSeries) {
          chartRefObj.addSeriesAsDrilldown(
            pointToDrilldown,
            drilldownSeries as Highcharts.SeriesOptionsType,
          )
        }
      } else {
        const len = chartRefObj.drilldownLevels?.length - targetLevel
        for (let i = 0; i < len; i++) {
          agGridRowGroupClickRef.current = true
          chartRefObj.drillUp()
        }
      }
    }
  }, [expandedRowGroupInfo, currentLevel])

  return (
    <Card elevation={0}>
      <CardHeader
        sx={{ p: 0 }}
        title={
          <Typography variant='h3'>
            {gfpaSummarySelectedSeriesName === 'Active'
              ? 'Portfolio'
              : gfpaSummarySelectedSeriesName}{' '}
            Security Details
          </Typography>
        }
      />
      <CardContent sx={{ p: 0 }}>
        <div
          className={`${reportType?.toLowerCase()}-security-details-chart`}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <HighchartsReact
            highcharts={Highcharts}
            ref={chartRef}
            options={chartOptions}
          />
        </div>
      </CardContent>
    </Card>
  )
}
